import { BluHtmlEditor, BluTextField } from '@bludata/components';
import { Stack } from '@mui/system';
import { ChangeEvent, FC, memo, useCallback, useState } from 'react';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { Else, If, Then } from 'react-if';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  defaultTextPrivacyPolicy,
  inferPrivacyPolicyMode,
  PrivacyPolicyMode,
} from '../../utils/default-privacy-policy';
import '../../../../styles/html-editor.css';

interface PrivacyInterface {
  disabled: boolean;
  privacyNotice: string;
  privacyPolicy: string;
  // 'notice' = informativa sul trattamento dei dati; 'policy' = privacy policy.
  section: 'notice' | 'policy';
}

export const Privacy: FC<PrivacyInterface> = memo(
  ({ disabled, privacyNotice, privacyPolicy, section }) => {
    // const [privacyNoticeCompiled, privacyPolicyCompiled] = useWatch({
    //   name: ["privacyNoticeCompiled", "privacyPolicyCompiled"],
    // });
    const { setValue, trigger, getValues } = useFormContext();
    const fontFamily = {
      default: 'Roboto',
      items: [
        {
          text: 'Roboto',
          value: 'Roboto',
          command: 'Font',
          subCommand: 'FontName',
        },

        {
          text: 'sans-serif',
          value: 'sans-serif',
          command: 'Font',
          subCommand: 'FontName',
        },
        {
          text: 'Poppins',
          value: 'Poppins',
          command: 'Font',
          subCommand: 'FontName',
        },
        {
          text: 'Helvetica',
          value: 'Helvetica',
          command: 'Font',
          subCommand: 'FontName',
        },
        {
          text: 'Arial',
          value: 'Arial',
          command: 'Font',
          subCommand: 'FontName',
        },
        {
          text: 'Comfortaa',
          value: 'Comfortaa',
          command: 'Font',
          subCommand: 'FontName',
        },
        // {
        //   text: "Montserrat",
        //   value: "Montserrat",
        //   command: "Font",
        //   subCommand: "FontName",
        // },
        // {
        //   text: "Noto Sans Georgian",
        //   value: "Noto Sans Georgian",
        //   command: "Font",
        //   subCommand: "FontName",
        // },
        // {
        //   text: "Source Sans Pro",
        //   value: "Source Sans Pro",
        //   command: "Font",
        //   subCommand: "FontName",
        // },
        // {
        //   text: "Work Sans",
        //   value: "Work Sans",
        //   command: "Font",
        //   subCommand: "FontName",
        // },
      ],
    };

    // useEffect(() => {
    //   const check =
    //     privacyNotice !== undefined &&
    //     privacyNotice !== "" &&
    //     !checkProvacyNotice.test(privacyNotice);

    //   if (check !== privacyNoticeCompiled) {
    //     setValue("privacyNoticeCompiled", check);
    //     trigger("privacyNoticeCompiled");
    //   }
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [privacyNotice, privacyNoticeCompiled]);

    // useEffect(() => {
    //   const check =
    //     privacyPolicy !== undefined &&
    //     privacyPolicy !== "" &&
    //     privacyPolicy !== "<div></div>";

    //   if (check !== privacyPolicyCompiled) {
    //     setValue("privacyPolicyCompiled", check);
    //     trigger("privacyPolicyCompiled");
    //   }
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [privacyPolicy, privacyPolicyCompiled]);

    const getPrivacyPolicyValue = useCallback(() => {
      // Leggo il valore "live" dalla form: dopo un cambio di modalità il prop
      // privacyPolicy (initialValues) resta fermo, getValues invece è aggiornato.
      const value = getValues('privacyPolicy') ?? privacyPolicy;
      if (!value || value === '') return '<p></p>';
      return value;
    }, [getValues, privacyPolicy]);

    const getPrivacyNoticeValue = useCallback(() => {
      if (!privacyNotice || privacyNotice === '') return '<p></p>';
      return privacyNotice;
    }, [privacyNotice]);

    const privacyPolicyMode = (useWatch({ name: 'privacyPolicyMode' }) ??
      'link') as PrivacyPolicyMode;

    const initialMode = (getValues('privacyPolicyMode') ??
      inferPrivacyPolicyMode(
        getValues('privacyPolicy') ?? privacyPolicy,
        getValues('privacyPolicyUrl')
      )) as PrivacyPolicyMode;

    // Tre bozze indipendenti, una per modalità: così spuntando una checkbox
    // o l'altra nessun contenuto va perso.
    // - linkInput: URL (modalità 'link', campo form privacyPolicyUrl)
    // - pasteDraft: testo incollato dal sito (modalità 'paste')
    // - templateDraft: testo di default modificato (modalità 'template')
    const [linkInput, setLinkInput] = useState<string>(
      () => (getValues('privacyPolicyUrl') as string) ?? ''
    );
    const [pasteDraft, setPasteDraft] = useState<string>(() =>
      initialMode === 'paste'
        ? ((getValues('privacyPolicy') ?? privacyPolicy) as string)
        : ''
    );
    const [templateDraft, setTemplateDraft] = useState<string>(() =>
      initialMode === 'template'
        ? ((getValues('privacyPolicy') ?? privacyPolicy) as string)
        : defaultTextPrivacyPolicy
    );

    const handlePrivacyPolicyModeChange = useCallback(
      (mode: PrivacyPolicyMode) => {
        setValue('privacyPolicyMode', mode);
        // L'URL vive nel campo privacyPolicyUrl; privacyPolicy contiene il testo
        // (paste/template) e resta vuoto in modalità link.
        if (mode === 'link') {
          setValue('privacyPolicy', '');
        } else {
          setValue('privacyPolicy', mode === 'paste' ? pasteDraft : templateDraft);
        }
        trigger(['privacyPolicy', 'privacyPolicyUrl']);
      },
      [pasteDraft, templateDraft, setValue, trigger]
    );

    return (
      <Stack direction="column" flex={1} gap={2}>
        {section === 'notice' && (
          <Stack flex="1" gap={1}>
            {!disabled && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Questa informativa è stata precompilata. Per completarla,
                sostituisci i campi evidenziati in giallo con le tue informazioni
                personali.
              </Typography>
            )}
            <If condition={disabled}>
              <Then>
                <div
                  style={{
                    width: '100%',
                    border: '1px solid var(--divider-color)',
                    height: '500px',
                    overflowY: 'scroll',
                    padding: '10px 20px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white',
                  }}
                  dangerouslySetInnerHTML={{ __html: getPrivacyNoticeValue() }}
                />
              </Then>
              <Else>
                <BluHtmlEditor
                  cssClass="privacy-html-editor"
                  fontFamily={fontFamily}
                  valueTemplate={getPrivacyNoticeValue()}
                  change={(event: any) => {
                    console.log('event', event?.value);

                    setValue('privacyNotice', event?.value ?? '<p></p>');
                    trigger('privacyNotice');
                  }}
                />
              </Else>
            </If>
          </Stack>
        )}
        {section === 'policy' && !disabled && (
          <Stack flex="1">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Scegli come gestire la privacy policy mostrata ai clienti durante
              la prenotazione. Puoi attivare una sola delle tre opzioni qui
              sotto. In tutti i casi, nella pagina di prenotazione il cliente
              troverà un collegamento alla privacy policy: cambia solo il modo
              in cui il testo viene fornito.
            </Typography>

            <FormControlLabel
              sx={{ m: 0, mt: 1, alignSelf: 'flex-start' }}
              control={
                <Checkbox
                  checked={privacyPolicyMode === 'link'}
                  onChange={(_event, checked) =>
                    checked && handlePrivacyPolicyModeChange('link')
                  }
                />
              }
              label={
                <Typography variant="body2">
                  <b>
                    Ho già una privacy policy sul mio sito web e voglio usare
                    quel link.
                  </b>{' '}
                  Inserisci l'indirizzo (URL) della pagina già pubblicata.
                  Durante la prenotazione il cliente vedrà un collegamento che
                  apre direttamente quella pagina sul tuo sito.
                </Typography>
              }
            />
            <BluTextField
              flex={1}
              label=""
              sx={{ pl: 1.5, pb: 2 }}
              disabled={privacyPolicyMode !== 'link'}
              placeholder="https://... (link alla privacy policy del tuo sito)"
              value={linkInput}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const raw = event?.target?.value ?? '';
                setLinkInput(raw);
                setValue('privacyPolicyUrl', raw);
                trigger('privacyPolicyUrl');
              }}
            />

            <FormControlLabel
              sx={{ m: 0, mt: 1, alignSelf: 'flex-start' }}
              control={
                <Checkbox
                  checked={privacyPolicyMode === 'paste'}
                  onChange={(_event, checked) =>
                    checked && handlePrivacyPolicyModeChange('paste')
                  }
                />
              }
              label={
                <Typography variant="body2">
                  <b>Ho già un testo di privacy policy e voglio mostrarlo qui.</b>{' '}
                  Incolla il testo completo nel riquadro sottostante. Durante la
                  prenotazione il cliente vedrà un collegamento che apre una
                  pagina interna a Blu Booking con esattamente il testo che hai
                  inserito.
                </Typography>
              }
            />
            <Stack
              sx={{
                ml: 1.5,
                mb: 1,
                // Editor non attivo: lo sfumo e lo rendo non cliccabile per
                // far capire che non è modificabile.
                ...(privacyPolicyMode !== 'paste' && {
                  opacity: 0.5,
                  pointerEvents: 'none',
                  filter: 'grayscale(0.4)',
                }),
              }}
            >
              {/* Editor 'paste': vuoto, per incollare la propria policy.
                  Disabilitato (grigio) quando la checkbox non è attiva. La key
                  rimonta l'editor al cambio di stato così l'attributo enabled
                  di Syncfusion viene applicato correttamente. */}
              <BluHtmlEditor
                key={privacyPolicyMode === 'paste' ? 'paste-on' : 'paste-off'}
                // Altezza dinamica (solo opzione 2): segue il contenuto fino al
                // limite max imposto via CSS su .privacy-html-editor-auto.
                cssClass="privacy-html-editor privacy-html-editor-auto"
                height="auto"
                fontFamily={fontFamily}
                enabled={privacyPolicyMode === 'paste'}
                readonly={privacyPolicyMode !== 'paste'}
                valueTemplate={pasteDraft}
                change={(event: any) => {
                  const value = event?.value ?? '<p></p>';
                  setPasteDraft(value);
                  if (privacyPolicyMode === 'paste') {
                    setValue('privacyPolicy', value);
                    trigger('privacyPolicy');
                  }
                }}
              />
            </Stack>

            <FormControlLabel
              sx={{ m: 0, mt: 1, alignSelf: 'flex-start' }}
              control={
                <Checkbox
                  checked={privacyPolicyMode === 'template'}
                  onChange={(_event, checked) =>
                    checked && handlePrivacyPolicyModeChange('template')
                  }
                />
              }
              label={
                <Typography variant="body2">
                  <b>
                    Non ho né un sito né un testo pronto: usa quello
                    predefinito.
                  </b>{' '}
                  Utilizza il modello di privacy policy già pronto e completalo
                  con i tuoi dati negli spazi evidenziati in giallo (ad esempio,
                  ragione sociale, indirizzo, telefono ed email). Durante la
                  prenotazione il cliente vedrà un collegamento che apre una
                  pagina interna a Blu Booking, con il testo predefinito così
                  personalizzato.
                </Typography>
              }
            />
            <Stack
              sx={{
                ml: 1.5,
                mb: 1,
                // Editor non attivo: lo sfumo e lo rendo non cliccabile per
                // far capire che non è modificabile.
                ...(privacyPolicyMode !== 'template' && {
                  opacity: 0.5,
                  pointerEvents: 'none',
                  filter: 'grayscale(0.4)',
                }),
              }}
            >
              {/* Editor 'template': precompilato col testo di default.
                  Disabilitato (grigio) quando la checkbox non è attiva. */}
              <BluHtmlEditor
                key={
                  privacyPolicyMode === 'template'
                    ? 'template-on'
                    : 'template-off'
                }
                cssClass="privacy-html-editor"
                fontFamily={fontFamily}
                enabled={privacyPolicyMode === 'template'}
                readonly={privacyPolicyMode !== 'template'}
                valueTemplate={templateDraft}
                change={(event: any) => {
                  const value = event?.value ?? '<p></p>';
                  setTemplateDraft(value);
                  if (privacyPolicyMode === 'template') {
                    setValue('privacyPolicy', value);
                    trigger('privacyPolicy');
                  }
                }}
              />
            </Stack>
          </Stack>
        )}
        {section === 'policy' && disabled && (
          <Stack flex="1">
            {/* Sola lettura: testo descrittivo della modalità attiva + solo il
                campo corrispondente alla modalità salvata. */}
            <Typography variant="body2" sx={{ mb: 1 }}>
              {privacyPolicyMode === 'link'
                ? 'Hai scelto di usare il link alla pagina pubblicata sul tuo sito web. Durante la prenotazione il cliente vedrà un collegamento che apre direttamente quella pagina.'
                : privacyPolicyMode === 'paste'
                ? 'Hai scelto di mostrare il testo che hai inserito. Durante la prenotazione il cliente vedrà un collegamento che apre una pagina interna a Blu Booking con la tua privacy policy completa.'
                : 'Hai scelto il testo predefinito, personalizzato con i tuoi dati. Durante la prenotazione il cliente vedrà un collegamento che apre una pagina interna a Blu Booking con il testo completo.'}
            </Typography>
            {privacyPolicyMode === 'link' ? (
              <BluTextField
                flex={1}
                label="URL privacy policy"
                disabled
                value={linkInput}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  border: '1px solid var(--divider-color)',
                  // Altezza dinamica: segue il contenuto fino a max 500px.
                  minHeight: '120px',
                  maxHeight: '500px',
                  overflowY: 'auto',
                  padding: '10px 20px',
                  boxSizing: 'border-box',
                  backgroundColor: 'white',
                }}
                dangerouslySetInnerHTML={{ __html: getPrivacyPolicyValue() }}
              />
            )}
          </Stack>
        )}
      </Stack>
    );
  }
);
